import { omit } from 'lodash';
import React from 'react';

import { Button } from '../../../../components/Button/Button';
import { Card } from '../../../../components/Card/Card';
import { KeyValueTable } from '../../../../components/KeyValueTable/KeyValueTable';
import { useModalPresenter } from '../../../../components/Modal/PresentedModal';
import { StatusPill } from '../../../../components/StatusPill/StatusPill';
import { graphql } from '../../../../generated';
import { t } from '../../../../locales/translate';
import { useQueryHandler } from '../../../../model/graphql';
import { CustomSenderAddressForm } from './CustomSenderAddressForm';

const CUSTOM_SENDER_ADDRESS = graphql(`
  query CustomSenderAddress($id: ID!) {
    shop(id: $id) {
      id
      useCustomSenderAddress
      customSenderAddress {
        name1
        name2
        name3
        address
        address2
        address3
        postCode
        contact
        countryCode
        phone
        email
        city
      }
    }
  }
`);

interface Props {
  shopId: string;
}

export function CustomSenderAddressCard({ shopId }: Props): JSX.Element | null {
  const { data, loading, refetch } = useQueryHandler(CUSTOM_SENDER_ADDRESS, {
    variables: { id: shopId },
  });

  const modal = useModalPresenter();
  if (!data?.shop) {
    return null;
  }

  const cleanData = omit(data.shop?.customSenderAddress, '__typename');

  const nonEmptyEntries = Object.entries(cleanData).filter(([, value]) => !!value);
  return (
    <Card
      loading={loading}
      elevation={1}
      title={
        <>
          <p>{t.model.shop.customSenderAddress}</p>
          <StatusPill status={data.shop.useCustomSenderAddress ? 'active' : 'inactive'} />
        </>
      }
      actions={
        <Button
          icon="edit"
          onClick={() =>
            modal.present(CustomSenderAddressForm, {
              onSave: refetch,
              customSenderAddress: cleanData,
              useCustomSenderAddress: !!data.shop?.useCustomSenderAddress,
              shopId,
            })
          }
        />
      }
    >
      {data.shop.useCustomSenderAddress && (
        <KeyValueTable
          items={nonEmptyEntries.map(([key, value]) => ({
            key: t.model.shop.customSenderAddressFields[key],
            value: key === 'countryCode' ? value?.toUpperCase() : value,
          }))}
        />
      )}
    </Card>
  );
}
